import { useReducer, useMemo, useCallback, useEffect } from 'react';
import { monthKey, monthLabel, monthLabelShort } from '../../../shared/utils/dates';

// Strip HTML so search matches the visible text, not markup (description/takeaways are rich text).
const stripTags = (s) => (s || '').replace(/<[^>]*>/g, ' ');

const initialState = { query: '', chapters: [], themes: [], months: [] };

function reducer(state, action) {
    switch (action.type) {
        case 'SET_QUERY':
            return { ...state, query: action.value };
        case 'TOGGLE': {
            const list = state[action.dimension];
            const next = list.includes(action.value)
                ? list.filter((v) => v !== action.value)
                : [...list, action.value];
            return { ...state, [action.dimension]: next };
        }
        case 'REMOVE':
            if (action.dimension === 'query') return { ...state, query: '' };
            return { ...state, [action.dimension]: state[action.dimension].filter((v) => v !== action.value) };
        case 'CLEAR':
            return initialState;
        default:
            return state;
    }
}

// Maps a chip's type back to the state key it removes from.
const CHIP_DIMENSION = { chapter: 'chapters', theme: 'themes', month: 'months', query: 'query' };

// URL <-> filter state, so a filtered view can be shared via its link. Array
// dimensions use repeated keys (?chapter=us&chapter=uk) because themes are free
// text — URLSearchParams handles the encoding without delimiter edge cases.
const URL_KEYS = ['q', 'chapter', 'theme', 'month'];

function stateFromUrl() {
    const p = new URLSearchParams(window.location.search);
    return {
        query: p.get('q') || '',
        chapters: p.getAll('chapter'),
        themes: p.getAll('theme'),
        months: p.getAll('month'),
    };
}

function urlFromState(state) {
    const p = new URLSearchParams(window.location.search);
    URL_KEYS.forEach((k) => p.delete(k)); // reset ours, keep unrelated params (e.g. utm_*)
    if (state.query) p.set('q', state.query);
    state.chapters.forEach((v) => p.append('chapter', v));
    state.themes.forEach((v) => p.append('theme', v));
    state.months.forEach((v) => p.append('month', v));
    const qs = p.toString();
    return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

export default function useEventFilters(program) {
    const events = program?.events ?? [];
    const chapterOptions = program?.chapters ?? [];
    const [state, dispatch] = useReducer(reducer, initialState, stateFromUrl);

    // Keep the URL in sync with the filters (replaceState: no history spam per keystroke).
    useEffect(() => {
        window.history.replaceState(null, '', urlFromState(state));
    }, [state]);

    const toggle = useCallback((dimension, value) => dispatch({ type: 'TOGGLE', dimension, value }), []);
    const setQuery = useCallback((value) => dispatch({ type: 'SET_QUERY', value }), []);
    const clearAll = useCallback(() => dispatch({ type: 'CLEAR' }), []);
    const removeChip = useCallback(
        (chip) => dispatch({ type: 'REMOVE', dimension: CHIP_DIMENSION[chip.type], value: chip.value }),
        []
    );

    // Filter options derived from the data in view.
    const options = useMemo(() => {
        const themes = [...new Set(events.flatMap((e) => e.themes || []))].sort();
        const months = [...new Set(events.map((e) => monthKey(e.date)))]
            .sort()
            .map((value) => ({ value, label: monthLabelShort(value) }));
        return { chapters: chapterOptions, themes, months };
    }, [events, chapterOptions]);

    const filtered = useMemo(() => {
        const q = state.query.trim().toLowerCase();
        return events.filter((ev) => {
            if (q) {
                // Search matches the displayed themes (categories) AND the underlying
                // topic labels, so a topic name still finds its events.
                const hay = [
                    ev.title,
                    ev.subtitle,
                    stripTags(ev.description),
                    stripTags(ev.takeaways),
                    ev.chapter?.name,
                    ...(ev.topics || []),
                    ...(ev.themes || []),
                    ...(ev.speakers || []).map((s) => s.name),
                ]
                    .join(' ')
                    .toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (state.chapters.length && !state.chapters.includes(ev.chapter?.slug)) return false;
            if (state.themes.length && !(ev.themes || []).some((t) => state.themes.includes(t))) return false;
            if (state.months.length && !state.months.includes(monthKey(ev.date))) return false;
            return true;
        });
    }, [events, state]);

    const activeCount = state.chapters.length + state.themes.length + state.months.length + (state.query ? 1 : 0);

    const chips = useMemo(() => {
        const out = [];
        state.themes.forEach((v) => out.push({ type: 'theme', value: v, label: v }));
        state.chapters.forEach((v) =>
            out.push({ type: 'chapter', value: v, label: chapterOptions.find((c) => c.slug === v)?.name || v })
        );
        state.months.forEach((v) => out.push({ type: 'month', value: v, label: monthLabel(v) }));
        if (state.query) out.push({ type: 'query', value: state.query, label: `"${state.query}"` });
        return out;
    }, [state, chapterOptions]);

    return { state, filtered, activeCount, chips, options, toggle, setQuery, removeChip, clearAll };
}
